import { create } from '@bufbuild/protobuf'
import { BidKind } from '@ebalance/proto/bfftypes/v1/bid_kind_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { BlockMandatoryPolicy } from '@ebalance/proto/bffuser/v1/bid_limit_model_pb'
import { UpdateBlockMandatoryPolicyRequestSchema } from '@ebalance/proto/bffuser/v1/bid_limit_service_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  Checkbox,
  ListItem,
  ListRoot,
  Text,
  toaster,
} from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { useUpdateBlockMandatoryPolicy } from '~/pages/AdminBidLimits/hooks/useBidLimitSettings'
import type { MatrixDraft as MatrixDraftType } from '~/pages/AdminBidLimits/models/bidLimitMatrix'
import { findMissingBlockCells } from '~/pages/AdminBidLimits/models/bidLimitMatrix'
import {
  FIRST_SAVE_EXPECTED_UPDATED_AT,
  MARKET_AREAS,
} from '~/pages/AdminBidLimits/models/constants'

type Props = {
  policy: BlockMandatoryPolicy | undefined
  blockDraft: MatrixDraftType
  /**
   * workflow-back-bar-readonly AC-17: ワークフローケースが完了済み/削除済みのとき true。
   * true のとき必須化トグルを disabled にする。
   */
  isReadonly?: boolean
}

export const BlockMandatoryToggleCard = ({
  policy,
  blockDraft,
  isReadonly = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { readonlyReason } = useWorkflowLockReasons()
  const { mutateAsync, isPending } = useUpdateBlockMandatoryPolicy()

  const enabled = policy?.enabled ?? false

  const handleToggle = async (newEnabled: boolean): Promise<void> => {
    try {
      await mutateAsync(
        create(UpdateBlockMandatoryPolicyRequestSchema, {
          enabled: newEnabled,
          // 初回保存 (policy 未取得) は epoch 番兵を送る (= backend が None 扱い)。
          expectedUpdatedAt:
            policy?.updatedAt ?? FIRST_SAVE_EXPECTED_UPDATED_AT,
        }),
      )
    } catch {
      // toaster は ecloud の CreateToasterReturn 型だが、tsconfig の
      // moduleResolution: node10 下では eslint の型解決が chakra→ark→zag の
      // exports サブパスを辿れず any に潰れる (tsc は skipLibCheck 込みで正しく
      // 解決し緑)。eslint 側のみの false positive のため局所 disable する。
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      toaster.create({
        type: 'error',
        title: t('page:adminBidLimits.blockMandatory.saveError'),
      })
    }
  }

  const missingCells = enabled ? findMissingBlockCells(blockDraft) : []

  const sideLabel = (side: Side): string =>
    side === Side.BUY
      ? t('page:adminBidLimits.matrix.buy')
      : t('page:adminBidLimits.matrix.sell')

  const kindLabel = (kind: BidKind): string => {
    if (kind === BidKind.MIN) return t('page:adminBidLimits.matrix.minPrice')
    if (kind === BidKind.MAX) return t('page:adminBidLimits.matrix.maxPrice')
    return t('page:adminBidLimits.matrix.maxQty')
  }

  const areaName = (code: number): string => {
    const area = MARKET_AREAS.find((a) => a.code === code)
    return area ? t(area.nameKey) : String(code)
  }

  return (
    <Box>
      <Text marginBottom={3} color="Text.midEmphasis" fontSize="sm">
        {t('page:adminBidLimits.blockMandatory.description')}
      </Text>
      <Checkbox
        checked={enabled}
        disabled={isPending || isReadonly}
        onCheckedChange={(d: { checked: boolean | 'indeterminate' }) =>
          handleToggle(d.checked === true)
        }
        title={readonlyReason}
      >
        <Box>
          <Text fontWeight="bold">
            {t('page:adminBidLimits.blockMandatory.label')}
          </Text>
          <Text color="Text.lowEmphasis" fontSize="sm">
            {t('page:adminBidLimits.blockMandatory.hint')}
          </Text>
        </Box>
      </Checkbox>

      {/* Positive alert: all cells decided */}
      {enabled && missingCells.length === 0 && (
        <Box marginTop={3}>
          <CompactAlert
            description={t('page:adminBidLimits.blockMandatory.allDecidedDesc')}
            status="success"
            title={t('page:adminBidLimits.blockMandatory.allDecided')}
          />
        </Box>
      )}

      {/* Negative alert: missing cells exist */}
      {enabled && missingCells.length > 0 && (
        <Alert
          marginTop={3}
          borderRadius="md"
          description={
            <>
              <Text fontSize="sm">
                {t('page:adminBidLimits.blockMandatory.missingDesc')}
              </Text>
              <Box as="details" marginTop={1}>
                {/* `<details>` は `list-style` を消さないとブラウザ既定の開閉標
                    (Chrome/Safari の黒い ▶) が出る。 8080 の
                    `.ecl-accordion-trigger` も `list-style:none` と
                    `::-webkit-details-marker{display:none}` の 2 つを持つ。
                    ここは Alert 内の小さな折りたたみなので、 カードの
                    `CardAccordion` とは違い chevron は付けず標だけ消す。 */}
                <Box
                  as="summary"
                  textStyle="captionStrong"
                  cursor="pointer"
                  css={{ '&::-webkit-details-marker': { display: 'none' } }}
                  listStyleType="none"
                >
                  {t(
                    'page:adminBidLimits.blockMandatory.missingDetailsToggle',
                    { count: missingCells.length },
                  )}
                </Box>
                <ListRoot
                  marginTop={1}
                  paddingLeft={4}
                  fontSize="sm"
                  css={{ columns: 2, columnGap: '24px' }}
                  listStyleType="disc"
                >
                  {missingCells.map((c) => (
                    <ListItem key={`${c.areaCode}-${c.side}-${c.kind}`}>
                      {areaName(c.areaCode)} / {sideLabel(c.side)} /{' '}
                      {kindLabel(c.kind)}
                    </ListItem>
                  ))}
                </ListRoot>
              </Box>
            </>
          }
          status="error"
          title={t('page:adminBidLimits.blockMandatory.missingTitle', {
            count: missingCells.length,
          })}
        />
      )}
    </Box>
  )
}
